import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { billingAPI } from '../../utils/crmApi';
import {
    CreditCardIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ArrowDownTrayIcon,
    PrinterIcon,
    ShareIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    ChartBarIcon,
    BanknotesIcon,
    ReceiptRefundIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Billing = () => {
    const { openDialog } = useDialog();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [stats, setStats] = useState(null);

    // Invoice status colors
    const getStatusColor = (status) => {
        const colors = {
            'draft': 'bg-muted text-foreground',
            'sent': 'bg-blue-100 text-blue-700',
            'viewed': 'bg-yellow-100 text-yellow-700',
            'paid': 'bg-green-100 text-green-700',
            'overdue': 'bg-red-100 text-red-700',
            'cancelled': 'bg-muted text-foreground',
            'refunded': 'bg-purple-100 text-purple-700'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Payment method icons
    const getPaymentMethodIcon = (method) => {
        const icons = {
            'credit_card': '💳',
            'bank_transfer': '🏦',
            'paypal': '💰',
            'stripe': '💳',
            'cash': '💵',
            'check': '📄',
            'crypto': '₿',
            'other': '💱'
        };
        return icons[method] || '💳';
    };

    useEffect(() => {
        loadInvoices();
        loadStats();
    }, [search, statusFilter, clientFilter, dateFilter, pagination.page]);

    const loadInvoices = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(clientFilter && { clientId: clientFilter }),
                ...(dateFilter && { dateRange: dateFilter })
            };

            const response = await billingAPI.getAll(params);
            if (response.data.success) {
                setInvoices(response.data.data);
                // Only update pagination if it exists in response, otherwise keep current
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load invoices:', error);
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await billingAPI.getStats();
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load billing stats:', error);
        }
    };

    const handleSelectInvoice = (invoiceId) => {
        setSelectedInvoices(prev => 
            prev.includes(invoiceId) 
                ? prev.filter(id => id !== invoiceId)
                : [...prev, invoiceId]
        );
    };

    const handleSelectAll = () => {
        if (selectedInvoices.length === invoices.length) {
            setSelectedInvoices([]);
        } else {
            setSelectedInvoices(invoices.map(invoice => invoice._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedInvoices.length === 0) return;
        
        const actionMessages = {
            'send': 'send',
            'mark_paid': 'mark as paid',
            'download': 'download',
            'delete': 'delete'
        };

        openDialog({
            title: `${actionMessages[action]} Invoices`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedInvoices.length} invoice(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await billingAPI.bulkAction(selectedInvoices, action);
                    toast.success(`${selectedInvoices.length} invoice(s) ${actionMessages[action]}ed successfully`);
                    setSelectedInvoices([]);
                    loadInvoices();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleInvoiceAction = async (invoiceId, action) => {
        try {
            await billingAPI.updateStatus(invoiceId, action);
            toast.success(`Invoice ${action}ed successfully!`);
            loadInvoices();
        } catch (error) {
            console.error(`Error ${action}ing invoice:`, error);
            toast.error(`Failed to ${action} invoice`);
        }
    };

    const handleDeleteInvoice = (invoiceId) => {
        openDialog({
            title: 'Delete Invoice',
            description: 'Are you sure you want to delete this invoice? This action cannot be undone.',
            confirmText: 'Delete Invoice',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await billingAPI.delete(invoiceId);
                    toast.success('Invoice deleted successfully');
                    loadInvoices();
                } catch (error) {
                    console.error('Error deleting invoice:', error);
                    toast.error('Failed to delete invoice');
                }
            }
        });
    };

    const handleSendInvoice = async (invoiceId) => {
        try {
            await billingAPI.sendInvoice(invoiceId);
            toast.success('Invoice sent successfully!');
            loadInvoices();
        } catch (error) {
            console.error('Error sending invoice:', error);
            toast.error('Failed to send invoice');
        }
    };

    const handleDownloadInvoice = async (invoiceId) => {
        try {
            const response = await billingAPI.download(invoiceId);
            // Create blob and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${invoiceId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast.error('Failed to download invoice');
        }
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'invoiceNumber', label: 'Invoice', sortable: true },
        { accessor: 'client', label: 'Client', sortable: true },
        { accessor: 'amount', label: 'Amount', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'dueDate', label: 'Due Date', sortable: true },
        { accessor: 'paymentMethod', label: 'Payment', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'viewed', label: 'Viewed' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'refunded', label: 'Refunded' }
    ];

    const dateFilterOptions = [
        { value: '', label: 'All Time' },
        { value: 'today', label: 'Today' },
        { value: 'this_week', label: 'This Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'this_quarter', label: 'This Quarter' },
        { value: 'this_year', label: 'This Year' },
        { value: 'last_30_days', label: 'Last 30 Days' },
        { value: 'last_90_days', label: 'Last 90 Days' }
    ];

    const getActionButton = (invoice) => {
        switch (invoice.status) {
            case 'draft':
                return (
                    <button
                        onClick={() => handleSendInvoice(invoice._id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        title="Send Invoice"
                    >
                        Send
                    </button>
                );
            case 'sent':
            case 'viewed':
                return (
                    <button
                        onClick={() => handleInvoiceAction(invoice._id, 'mark_paid')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Mark as Paid"
                    >
                        Mark Paid
                    </button>
                );
            case 'overdue':
                return (
                    <button
                        onClick={() => handleSendInvoice(invoice._id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                        title="Send Reminder"
                    >
                        Remind
                    </button>
                );
            default:
                return null;
        }
    };

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('en-EU', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const getDaysOverdue = (dueDate) => {
        const today = new Date();
        const due = new Date(dueDate);
        const diffTime = today - due;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Billing & Invoicing</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage invoices, payments, and financial reports
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedInvoices.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('send')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                            >
                                <ShareIcon className="h-4 w-4 mr-1" />
                                Send
                            </button>
                            <button
                                onClick={() => handleBulkAction('mark_paid')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Mark Paid
                            </button>
                            <button
                                onClick={() => handleBulkAction('download')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted disabled:opacity-50"
                            >
                                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                Download
                            </button>
                            <button
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                    <Link
                        to="/billing/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create Invoice
                    </Link>
                </div>
            </div>

            {/* Financial Overview */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                                <p className="text-2xl font-semibold text-foreground">
                                    {formatCurrency(stats.totalRevenue)}
                                </p>
                                <p className="text-xs text-green-600 flex items-center">
                                    <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                                    +{stats.revenueGrowth}% from last month
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Paid Invoices</p>
                                <p className="text-2xl font-semibold text-foreground">
                                    {formatCurrency(stats.paidAmount)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {stats.paidInvoices} invoices
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ClockIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                                <p className="text-2xl font-semibold text-foreground">
                                    {formatCurrency(stats.pendingAmount)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {stats.pendingInvoices} invoices
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Overdue</p>
                                <p className="text-2xl font-semibold text-foreground">
                                    {formatCurrency(stats.overdueAmount)}
                                </p>
                                <p className="text-xs text-red-600">
                                    {stats.overdueInvoices} invoices
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Client</label>
                        <input
                            type="text"
                            value={clientFilter}
                            onChange={(e) => setClientFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            placeholder="Search by client name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Date Range</label>
                        <select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {dateFilterOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setStatusFilter('');
                                setClientFilter('');
                                setDateFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-lg hover:bg-muted"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={invoices} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search invoices by number, client, or amount..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="text-center">
                                                <CreditCardIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                                <h3 className="mt-2 text-sm font-medium text-foreground">No invoices found</h3>
                                                <p className="mt-1 text-sm text-muted-foreground">
                                                    Get started by creating your first invoice.
                                                </p>
                                                <Link
                                                    to="/billing/new"
                                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                                >
                                                    <PlusIcon className="h-5 w-5 mr-2" />
                                                    Create Invoice
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    invoices.map((invoice) => {
                                        const daysOverdue = getDaysOverdue(invoice.dueDate);
                                        return (
                                            <TableRow key={invoice._id}>
                                                <TableCell className="w-12">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedInvoices.includes(invoice._id)}
                                                        onChange={() => handleSelectInvoice(invoice._id)}
                                                        className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center">
                                                        <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
                                                        <div>
                                                            <Link
                                                                to={`/billing/${invoice._id}`}
                                                                className="text-sm font-medium text-foreground hover:text-blue-600"
                                                            >
                                                                {invoice.invoiceNumber}
                                                            </Link>
                                                            <div className="text-xs text-muted-foreground">
                                                                {new Date(invoice.createdAt).toLocaleDateString()}
                                                            </div>
                                                            {daysOverdue > 0 && invoice.status === 'overdue' && (
                                                                <div className="text-xs text-red-600">
                                                                    {daysOverdue} days overdue
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm text-foreground">{invoice.clientName}</div>
                                                    <div className="text-xs text-muted-foreground">{invoice.clientEmail}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm font-medium text-foreground">
                                                        {formatCurrency(invoice.totalAmount, invoice.currency)}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {invoice.items?.length || 0} items
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge 
                                                        status={invoice.status}
                                                        className={getStatusColor(invoice.status)}
                                                    >
                                                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                                    </StatusBadge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm text-foreground">
                                                        {new Date(invoice.dueDate).toLocaleDateString()}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {Math.ceil((new Date(invoice.dueDate) - new Date()) / (1000 * 60 * 60 * 24))} days
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center">
                                                        <span className="text-lg mr-2">{getPaymentMethodIcon(invoice.paymentMethod)}</span>
                                                        <span className="text-sm text-foreground capitalize">
                                                            {invoice.paymentMethod?.replace('_', ' ')}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {getActionButton(invoice)}
                                                        <button
                                                            onClick={() => handleDownloadInvoice(invoice._id)}
                                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                            title="Download"
                                                        >
                                                            <ArrowDownTrayIcon className="h-4 w-4" />
                                                        </button>
                                                        <Link
                                                            to={`/billing/${invoice._id}`}
                                                            className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                            title="View"
                                                        >
                                                            <EyeIcon className="h-4 w-4" />
                                                        </Link>
                                                        <Link
                                                            to={`/billing/${invoice._id}/edit`}
                                                            className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                            title="Edit"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDeleteInvoice(invoice._id)}
                                                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                            title="Delete"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </table>
                    </div>
                    
                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-foreground">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>
        </div>
    );
};

export default Billing;